import { retiredPricingDetails } from '../../utils/quotationLinePricingFacts';
import { normalizePackStructure, packStatesSomething } from '../../utils/packConversion';
import { itemNameAfterProductPick } from '../../utils/quotationItemNameAfterPick';

/**
 * What a line's `pricingDetails` become when a reviewer picks a Master Catalogue
 * row for it in the quotation page's catalogue search popup.
 *
 * ── The defect this closes ───────────────────────────────────────────────────
 * The pick built the new line as `{...currentItem, itemName, unitPrice, uom, …}`
 * and never touched `pricingDetails`, so the line went on describing the row it
 * had just REPLACED:
 *   - `masterCatalogueId` still named the old row, so the line's catalogue link
 *     would have highlighted a row that no longer priced it;
 *   - `displayEquivalents` still carried the old product's pack;
 *   - `conversionAnchor` still held the old price in the old unit, and a Unit
 *     change restates FROM the anchor — so the next Unit change put the replaced
 *     row's price back on the line.
 * The server's supplier pick had the same shape until TECH_DEBT #330 / D22; this
 * is the same answer through the same lists (`utils/quotationLinePricingFacts`).
 *
 * ── What the line carries instead ────────────────────────────────────────────
 * The picked row's id and source, a `catalogueSearchPick` mark saying the row was
 * picked by hand and at which units, and an anchor at the UNROUNDED cost just
 * chosen — the fixed point a Unit change restates from, re-seeded as a supplier
 * pick re-seeds it. `catalogueSearchPick.rowName` also carries the picked row's
 * OWN name — since 2026-09-22 (below) the pick no longer writes it onto the
 * line's `itemName`, so this is INTERNAL pricing metadata recording which row
 * priced the line now. It must never reach a customer-facing document.
 *
 * ── And the picked row's pack (QUO-00054) ────────────────────────────────────
 * The search endpoint now sends the pack the row states, built on the server by
 * the owner that rebuilds any line's pack from its row
 * (`quotationPackStructure.packStructureOfCatalogueRow`). It goes on the line
 * as `displayEquivalents.packStructure`, where the Unit dropdown and the per-kg
 * / per-L figures read a pack from, so the next Unit change restates through
 * the product the line is now priced from — D22's rule, which the popup could
 * not follow while it had no pack to state. A row that states none still gets
 * no block, and the Unit dropdown reads the pack from the item name, as it does
 * for any line that carries none.
 */
/**
 * The `catalogueSearchPick` mark: the line was priced from a catalogue row picked by hand, at these
 * units. THE ONE spelling of it, for both ways a person prices a line from a row: the search popup's
 * pick (above) and "+ Add this item to Master Catalogue" (`addCatalogueItemFromLine`, whose row does
 * not exist until an admin approves it, so it carries this mark and no row id). The server reads it to
 * withdraw the replaced match's notes (D119.5) and the quoted pack names `rowName` (D119.12).
 */
export function catalogueSearchPickMark({
  rowUnit,
  pricedPer,
  rowName,
}: {
  rowUnit?: string | null;
  pricedPer?: string | null;
  rowName?: string | null;
}): { rowUnit: string | null; pricedPer: string | null; rowName: string | null } {
  const text = (v: string | null | undefined) => (typeof v === 'string' && v.trim() ? v.trim() : null);
  return { rowUnit: text(rowUnit), pricedPer: text(pricedPer), rowName: text(rowName) };
}

/**
 * The catalogue row a pick priced the line from, by its mark's `rowName`: only when the line names a
 * row (`masterCatalogueId`), as the server's `pricedFromFacts` reads it. An item added to the catalogue
 * from the line carries the mark and no row until an admin approves it, so it names none (D119.5).
 * The one reader of the name, for the grid tooltip and the match note.
 */
export function catalogueSearchPickRowName(pricingDetails: unknown): string | null {
  if (!pricingDetails || typeof pricingDetails !== 'object') return null;
  const details = pricingDetails as Record<string, unknown>;
  if (!details.masterCatalogueId) return null;
  const pick = details.catalogueSearchPick;
  const name = pick && typeof pick === 'object' ? (pick as Record<string, unknown>).rowName : undefined;
  return typeof name === 'string' && name.trim() ? name.trim() : null;
}

export function pricingDetailsForCataloguePick({
  replaced,
  row,
  pricedPer,
  anchor,
}: {
  /** The details of the line being replaced, read before it is. */
  replaced: unknown;
  row: {
    _id: string;
    source?: string | null;
    unit?: string | null;
    item_name?: string | null;
    packStructure?: unknown;
    /** The size of ONE piece as the server resolved it (`mapRowToAiCandidate`'s ranking). */
    packFacts?: { unitSize?: string | null } | null;
    /** The hand-entry size column, read only when `packFacts` is absent. */
    unit_size?: string | null;
    /** Blank (null/undefined) marks the line for the "Does this item have VAT?" popup. */
    vat_applicable?: boolean | null;
    /** The VAT inside the row's cost, as the SERVER stated it (`MasterCatalogueItem.costVatPercent`). */
    costVatPercent?: number | null;
  };
  /** The unit the line is priced per after the pick. */
  pricedPer: string | null | undefined;
  /** The line's conversion anchor at its new cost, or null when there is none to state. */
  anchor: object | null;
}): Record<string, unknown> {
  const rowUnit = typeof row.unit === 'string' && row.unit.trim() ? row.unit.trim() : null;
  const per = typeof pricedPer === 'string' && pricedPer.trim() ? pricedPer.trim() : null;
  // The row's own name, kept here as INTERNAL pricing metadata only (2026-09-22
  // client call, below) — never a customer-facing field. Now that the pick no
  // longer overwrites the line's name, this is the only place left recording
  // which row priced the line, for the grid's own tooltip.
  const rowName = typeof row.item_name === 'string' && row.item_name.trim() ? row.item_name.trim() : null;
  const details: Record<string, unknown> = {
    ...retiredPricingDetails(replaced),
    masterCatalogueId: row._id,
    catalogueSource: row.source ?? null,
    catalogueSearchPick: catalogueSearchPickMark({ rowUnit, pricedPer: per, rowName }),
    // Owner decision 2026-09-24 ("one Trinidad VAT rate"): a row whose VAT is
    // BLANK (nobody has said yes or no) is priced without VAT and this line is
    // marked so the grid's "VAT?" badge opens `CatalogueVatPrompt` for it —
    // never written `true` for a `false`/`true` row, so the badge shows only
    // where the question is genuinely unanswered.
    catalogueVatUnset: row.vat_applicable == null,
  };
  // The VAT inside the cost just chosen (D82 pre-push review), recorded where
  // the cost is set, as every server writer records it — so the PO nets this
  // cost at the percent it was priced with, not at whatever Settings says when
  // the PO is raised. The server's fact for this row (search / get-by-id / the
  // VAT answer), copied as is: no VAT maths here. A row that states none
  // leaves it absent — the reader's documented fallback, never a guess.
  if (row.costVatPercent !== undefined) details.costVatPercent = row.costVatPercent;
  if (anchor) details.conversionAnchor = anchor;
  if (packStatesSomething(row.packStructure)) {
    const equivalents: Record<string, unknown> = {
      // Read as a server block by `getAlternateQuotationPricing`: the pack was
      // built on the server, and only the pack is stated — no ratios, which are
      // per-unit and would describe the unit at the pick rather than the product.
      serverComputed: true,
      source: 'catalogue_search_pick',
      uom: per,
      packStructure: normalizePackStructure(row.packStructure),
    };
    // The piece size as TEXT, the field generation writes (`unitSize`): the
    // Packing Size column shows it, and the client-visible packaging sentence
    // (`packConversion.packagingRemark`, owner decision 2026-09-26) reads it —
    // "Case of 24 × 500 ML", which the pack's litres alone cannot spell.
    const unitSize = row.packFacts ? row.packFacts.unitSize : row.unit_size;
    if (typeof unitSize === 'string' && unitSize.trim()) equivalents.unitSize = unitSize.trim();
    details.displayEquivalents = equivalents;
  }
  return details;
}

/** The refusal's machine-readable code — callers word it by this, never by prose. */
export const VAT_RATE_CHANGED = 'VAT_RATE_CHANGED';
export const VAT_RATE_CHANGED_MESSAGE =
  'The VAT rate in Settings changed after this page was opened, so this line was not priced. Reload the page and try again.';

/**
 * Whether a VAT = Yes row's served fact and the page's loaded rate are ONE
 * reading of the Settings rate (D82 pre-push review).
 *
 * The page grosses a catalogue cost at the rate it loaded when it mounted
 * (`catalogueRowCostPrice(item, vatRatePercent)`), while the fact recorded
 * beside that cost (`costVatPercent`) is the rate the SERVER read when it
 * served the row / answered the VAT PATCH / receipted a submission. Settings
 * changed in between (12.5 -> 15) and the line cost TT$2.25 while the fact said
 * 15, so the PO netted 2.25 / 1.15 = 1.96, not 2.00; cleared, the fact said
 * null and the PO printed 2.25 as before VAT. A comparison only — no VAT maths.
 *
 * Only a VAT = Yes row uses the rate: a No row (fact 0) and a blank row (fact
 * null) always agree. A row that states no fact (absent) records none, so there
 * is nothing to disagree with. Both null (no rate anywhere) agrees here — the
 * cost owner already refuses that as VAT rate not set.
 */
export function costVatAgreesWithLoadedRate(
  row: { vat_applicable?: boolean | null; costVatPercent?: number | null },
  loadedVatRatePercent: number | null
): boolean {
  if (row.vat_applicable !== true || row.costVatPercent === undefined) return true;
  const loaded =
    typeof loadedVatRatePercent === 'number' && Number.isFinite(loadedVatRatePercent) ? loadedVatRatePercent : null;
  return row.costVatPercent === loaded;
}

/** The refusal when `costVatAgreesWithLoadedRate` says no — coded, so the page words it by code. */
export function vatRateChangedError(message: string = VAT_RATE_CHANGED_MESSAGE): Error & { code: string } {
  return Object.assign(new Error(message), { code: VAT_RATE_CHANGED });
}

/**
 * The name a quotation line keeps after a catalogue-search pick prices it.
 *
 * ── The defect this closes ───────────────────────────────────────────────────
 * The pick built the new line as `{...currentItem, itemName: item.item_name, …}`,
 * so pricing a line from the Master Catalogue silently renamed it to the picked
 * row's name — replacing the vessel's own wording with whatever the catalogue
 * row happened to be called.
 *
 * ── The call it came from ────────────────────────────────────────────────────
 * United Ship Chandlers, 2026-09-22 (JP and Deborah). JP: "You click the item
 * and it's going to replace the item description. I don't want to replace the
 * vessel's item description. I just want to replace the price." Deborah:
 * "Sometimes these people have their own description … we try not to change
 * their item name because they're familiar with it that way." Kaushal,
 * confirming: two things are always kept through a pick — the name and the
 * requested quantity (the quantity's own safety net is QUO-00054 / D51, above
 * — this function does not touch it).
 *
 * A line added blank — never named — has no vessel wording to protect, and
 * leaving it blank would ship a nameless quotation line, so a blank or
 * whitespace-only name still takes the picked row's.
 *
 * A thin wrapper: the rule itself moved to the mirrored
 * `utils/quotationItemNameAfterPick.itemNameAfterProductPick` (owner audit,
 * 2026-09-25) once the quotation page's OTHER pick — the merged panel's
 * SUPPLIER row (`quotationLineSupplierPrice.resolveListedSupplierPick`,
 * server) — needed the identical answer for the identical reason (F1: it too
 * can now name a genuinely different product, not only a different vendor).
 * Kept here, under its original name, so this file's own callers and tests
 * are unaffected.
 */
export function itemNameAfterCataloguePick({
  currentName,
  rowName,
}: {
  /** The line's own name before the pick, exactly as it is stored. */
  currentName: string | null | undefined;
  /** The picked row's name. */
  rowName: string | null | undefined;
}): string {
  return itemNameAfterProductPick({ currentName, pickedName: rowName });
}

/**
 * Whether a line still needs a Master Catalogue row, right after a catalogue
 * search pick prices it.
 *
 * ── The consequence this closes ──────────────────────────────────────────────
 * A pick sets `pricingDetails.masterCatalogueId` — the catalogue CAN price this
 * line now — but nothing used to touch `notInCatalogue` or its companion
 * `catalogueNearMisses`, so they stayed at whatever Step 1 last wrote. For a
 * line a reviewer is fixing BY HAND, that is usually `notInCatalogue: true`,
 * and nothing downstream ever re-runs Step 1 to correct it: `updateQuotation`
 * carries `items` through as given, and the only other client path that
 * clears the flag is `handleRefreshLinePrice`'s own lookup answering false.
 * Left stale, two things follow:
 *   - cosmetic: the Item Name cell stays pink with "Not in catalogue" right
 *     after the row that prices it was picked;
 *   - data: `quotationCatalogueHarvest.planHarvest` only harvests a line where
 *     `notInCatalogue === true` — TECH_DEBT #197's guard against the
 *     duplicate-row problem (557 of 4,480 products already carry two or more).
 *     A stale-true line reaching Finalized/Rejected would get a NEW
 *     `quotation_historical` row written for a product the catalogue already
 *     has. Before `itemNameAfterCataloguePick` (above), that row happened to
 *     carry the picked row's own name, which collided with the existing row
 *     on the unique index and read as already-present; keeping the vessel's
 *     name removes that accidental collision, so the stale flag now produces
 *     a genuine duplicate — the exact pollution the 2026-09-22 client call
 *     was emphatic about ("I don't want garbage in my master catalogue").
 * A pick IS the catalogue answering, so both clear — the same pair
 * `handleRefreshLinePrice` sets together when ITS answer is false.
 *
 * The count clears to 0, not to `undefined`. Both reach the same stored value,
 * but only by different routes: `undefined` is dropped by `JSON.stringify` on
 * the way out, so the field arrives ABSENT and lands on 0 through the schema
 * default (`models/Quotation.js`, `catalogueNearMisses: {default: 0, min: 0}`).
 * That is a correct answer resting on two things neither this file nor its test
 * can see. 0 states it here, matches the field's own type, and is what
 * `handleRefreshLinePrice` writes when its lookup answers none.
 *
 * ── Two more fields found stale, same audit (owner decisions 2026-09-24) ────
 * `handleSearchSelect` spreads `{...currentItem}` before applying the pick, so
 * two more top-level fields rode through describing the REPLACED item:
 *
 *   - `vendorOptions` — the alternate-vendor list `quotationGeneration`
 *     embedded for the OLD product at generation. It is not rendered by this
 *     page, but `buildConfirmedItemsFromQuotation` carries it onto a
 *     Confirmed Order line, whose Cost columns read
 *     `vendorOptions[selectedVendor]` (TECH_DEBT #204) — so a line repriced
 *     from an unrelated catalogue row could confirm at a stale entry that
 *     happens to share the new supplier's NAME with the old product's list.
 *     Cleared to `[]`: a hand pick names one supplier, not a comparison.
 *   - `unitMismatch` / `unitRequested` — stamped by the PRICING ENGINE
 *     (`quotationGeneration.resolveQuotedUnit`,
 *     BUSINESS_RULES#quoted-unit-vs-rfq-ask) to mean "no real ratio was on file between the RFQ's unit
 *     and the OLD product's price", and rendered as the amber Unit / Unit
 *     Cost Price flag with a tooltip naming `unitRequested`. By the time a
 *     catalogue pick reaches this function it has already cleared that bar
 *     for the NEW product — `handleSearchSelect`'s restatement block either
 *     restated the quantity through the picked row's pack, needed no
 *     restatement, or refused the pick outright (the alert, never reaching
 *     here) — so a mismatch flag surviving the pick could only be lying
 *     about a row that is no longer on the line.
 */
export function catalogueFlagsAfterCataloguePick(): {
  notInCatalogue: false;
  catalogueNearMisses: number;
  vendorOptions: never[];
  unitMismatch: false;
  unitRequested: null;
  unitMismatchReason: null;
} {
  return {
    notInCatalogue: false,
    catalogueNearMisses: 0,
    vendorOptions: [],
    unitMismatch: false,
    unitRequested: null,
    unitMismatchReason: null,
  };
}
